import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Hook to fetch FE Base list
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeOfficialsMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    catogary: '',
    director_name: '',
    directors_phone_no: '',
    director_email: '',
    direct_address: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'catogary', field: 'catogary', headerName: 'Category', width: 150 },
    { id: 'director_name', field: 'director_name', headerName: 'Director Name', width: 200 },
    { id: 'directors_phone_no', field: 'directors_phone_no', headerName: 'Phone Number', width: 160 },
    { id: 'director_email', field: 'director_email', headerName: 'Email', width: 220 },
    { id: 'direct_address', field: 'direct_address', headerName: 'Address', width: 300 },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchOfficials = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-officials/`)
      .then(response => {
        // Map each record and find FE Base name for display
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            catogary: item.catogary,
            director_name: item.director_name,
            directors_phone_no: item.directors_phone_no,
            director_email: item.director_email,
            direct_address: item.direct_address,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Failed to fetch Officials:', error);
        setSnackbarMessage('Failed to load Officials.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchOfficials();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      financial_establishment: '',
      catogary: '',
      director_name: '',
      directors_phone_no: '',
      director_email: '',
      direct_address: '',
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);

    // Find FE Base record by displayed name or id to set correct id for form dropdown
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormData({
      financial_establishment: feBaseObj ? feBaseObj.id : '',
      catogary: item.catogary,
      director_name: item.director_name,
      directors_phone_no: item.directors_phone_no,
      director_email: item.director_email,
      direct_address: item.direct_address,
    });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-officials/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Official deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchOfficials();
      })
      .catch(error => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Official.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment) errors.financial_establishment = 'FE Base is required';
    if (!formData.catogary?.trim()) errors.catogary = 'Category is required';
    if (!formData.director_name?.trim()) errors.director_name = 'Director name is required';
    if (!formData.directors_phone_no?.toString().trim()) errors.directors_phone_no = 'Phone number is required';
    if (!formData.director_email?.trim()) errors.director_email = 'Email is required';
    if (!formData.direct_address?.trim()) errors.direct_address = 'Address is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      financial_establishment: formData.financial_establishment,
      catogary: formData.catogary,
      director_name: formData.director_name,
      directors_phone_no: formData.directors_phone_no,
      director_email: formData.director_email,
      direct_address: formData.direct_address,
    };
    try {
      setUploading(true);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-officials/${selectedItem.id}/`, payload);
        setSnackbarMessage('Official updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-officials/`, payload);
        setSnackbarMessage('Official added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchOfficials();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit Official.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          FE Officials
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }} />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Official' : 'Add Official'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="FE Base"
            fullWidth
            value={formData.financial_establishment}
            error={!!formErrors.financial_establishment}
            helperText={formErrors.financial_establishment}
            onChange={e => setFormData({ ...formData, financial_establishment: e.target.value })}
            disabled={uploading}
          >
            {feBaseList.map(fe => (
              <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Category"
            fullWidth
            value={formData.catogary}
            error={!!formErrors.catogary}
            helperText={formErrors.catogary}
            onChange={e => setFormData({ ...formData, catogary: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Director Name"
            fullWidth
            value={formData.director_name}
            error={!!formErrors.director_name}
            helperText={formErrors.director_name}
            onChange={e => setFormData({ ...formData, director_name: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Director's Phone Number"
            fullWidth
            type="number"
            value={formData.directors_phone_no}
            error={!!formErrors.directors_phone_no}
            helperText={formErrors.directors_phone_no}
            onChange={e => setFormData({ ...formData, directors_phone_no: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Director Email"
            fullWidth
            type="email"
            value={formData.director_email}
            error={!!formErrors.director_email}
            helperText={formErrors.director_email}
            onChange={e => setFormData({ ...formData, director_email: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="Address"
            multiline
            rows={3}
            fullWidth
            value={formData.direct_address}
            error={!!formErrors.direct_address}
            helperText={formErrors.direct_address}
            onChange={e => setFormData({ ...formData, direct_address: e.target.value })}
            disabled={uploading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>{isEditMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Official?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
